import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  Send,
  Trash2,
  WifiOff,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import RichEditor from '@/client/components/cms/RichEditor';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import { MEDIUM_LABEL as MEDIA_MEDIUM_LABEL } from '@/client/types/media';
import type { SocialPlatform as MediaSocialPlatform, TraditionalMedium } from '@/client/types/media';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import { ActionIconButton } from './components/ActionIconButton';
import { DeleteConfirmDialog } from './components/marketing-ui';
import {
  useApproveContentMutation,
  useContentItemQuery,
  useContentVariantsQuery,
  useCreateContentMutation,
  useCreateVariantMutation,
  useDeleteContentMutation,
  useDeleteVariantMutation,
  useQueueVariantsMutation,
  useSetContentStatusMutation,
  useSubmitContentMutation,
  useUpdateContentMutation,
  useUpdateVariantMutation,
} from '@/client/api/marketing/studio';
import { useDestinationsQuery } from '@/client/api/marketing/sources';
import { usePlaysQuery, useSeriesQuery } from '@/client/api/marketing/plays';
import type { ApiContentVariant } from '@/client/types/marketing/api';
import { refFromApi, sameSource, sourceKey, type SourceRef } from '@/client/types/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  CONTENT_FORMAT_LABEL,
  CONTENT_STATUS_LABEL,
  CONTENT_TYPE_LABEL,
  SOCIAL_FORMATS,
  TRADITIONAL_FORMATS,
  defaultFormatForPlatform,
  type ContentFormat,
  type ContentStatus,
  type ContentType,
} from '@/client/types/marketing';
import { PLAY_STATUS_LABEL, MARKETING_PLAY_TAG_LABEL, MARKETING_PLAY_TAGS, type MarketingPlayTag } from '@/client/types/marketing/strategy';
import { cn } from '@/lib/utils';

/**
 * Studio → Content editor, on real data.
 *
 * The status control here only offers the ungated moves — idea, draft, archived.
 * Everything else is a gate: `in_review` is reached by submitting, `approved` by
 * an approver with the permission, `published` by the worker. The server refuses
 * the rest by name, and offering them in a dropdown would produce a control that
 * looks like it works and silently does not.
 *
 * Variants are saved individually rather than as part of the content body,
 * because that is how the API models them and because a variant already queued
 * must not be silently rewritten by an unrelated edit to the master copy.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

/** Statuses an author may set directly. The rest are earned, not chosen. */
type OpenStatus = 'idea' | 'draft' | 'archived';
const OPEN_STATUSES: OpenStatus[] = ['idea', 'draft', 'archived'];

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

const toDatetimeLocal = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromDatetimeLocal = (value: string) => (value ? new Date(value).toISOString() : null);

const asTags = (value: unknown): string[] =>
  Array.isArray(value) ? (value as unknown[]).map(String) : [];

const knownPlayTags = (value: unknown): MarketingPlayTag[] =>
  asTags(value).filter((t): t is MarketingPlayTag =>
    (MARKETING_PLAY_TAGS as readonly string[]).includes(t),
  );

const TAG_CHIP_OPTIONS = MARKETING_PLAY_TAGS.map((t) => ({
  value: t,
  label: MARKETING_PLAY_TAG_LABEL[t],
}));

type ContentFormErrors = {
  title?: string;
  tags?: string;
};

const ContentEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const mciid = isNew ? NaN : Number(id);

  const contentQ = useContentItemQuery(mciid, !isNew);
  const variantsQ = useContentVariantsQuery(mciid, !isNew);
  const destinations = useDestinationsQuery();
  const plays = usePlaysQuery({ limit: 100 });
  const seriesQ = useSeriesQuery({ limit: 100 });

  const createContent = useCreateContentMutation();
  const updateContent = useUpdateContentMutation();
  const deleteContent = useDeleteContentMutation();
  const setStatusMutation = useSetContentStatusMutation();
  const submitContent = useSubmitContentMutation();
  const approveContent = useApproveContentMutation();
  const queueVariants = useQueueVariantsMutation();
  const createVariant = useCreateVariantMutation();
  const updateVariant = useUpdateVariantMutation();
  const deleteVariant = useDeleteVariantMutation();

  const existing = contentQ.data ?? null;
  const variants: ApiContentVariant[] = variantsQ.data?.items ?? [];

  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<ContentType>('campaign');
  const [playId, setPlayId] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState<MarketingPlayTag[]>([]);
  const [formErrors, setFormErrors] = useState<ContentFormErrors>({});
  const [scheduledAt, setScheduledAt] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addSource, setAddSource] = useState('');
  const [savingVariant, setSavingVariant] = useState(false);

  // Load once the item arrives, and again if the id changes. Not on every
  // refetch: that would stamp on whatever is being typed.
  useEffect(() => {
    if (!existing) return;
    setTitle(existing.title);
    setContentType(existing.content_type as ContentType);
    setPlayId(existing.play_id != null ? String(existing.play_id) : '');
    setSeriesId(existing.series_id != null ? String(existing.series_id) : '');
    setExcerpt(existing.excerpt ?? '');
    setBody(existing.body ?? '');
    setTags(knownPlayTags(existing.tags));
    setScheduledAt(toDatetimeLocal(existing.scheduled_at));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.mciid]);

  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Marketing', href: '/admin/marketing' },
    { label: 'Studio' },
    { label: 'Content', href: '/admin/marketing/studio/content' },
    { label: isNew ? 'New' : (existing?.title ?? 'Edit') },
  ]);

  const playOptions = useMemo(
    () =>
      [...(plays.data?.items ?? [])].sort((a, b) => {
        const rank = (s: string) => (s === 'active' ? 0 : s === 'draft' || s === 'paused' ? 1 : 2);
        return rank(a.status) - rank(b.status) || a.name.localeCompare(b.name);
      }),
    [plays.data],
  );

  /** Every account and outlet a variant may target. */
  const destinationOptions = useMemo(() => {
    const social = (destinations.data?.accounts ?? [])
      .filter((a) => a.use_as_destination)
      .map((a) => ({
        key: `account:${a.said}`,
        ref: { sourceKind: 'account' as const, sourceId: a.said },
        kind: 'social' as const,
        label: `${a.display_name} · ${a.handle}`,
        platform: a.platform as MediaSocialPlatform,
        medium: undefined as TraditionalMedium | undefined,
      }));
    const traditional = (destinations.data?.outlets ?? [])
      .filter((o) => o.use_as_destination && !o.do_not_pitch)
      .map((o) => ({
        key: `outlet:${o.moid}`,
        ref: { sourceKind: 'outlet' as const, sourceId: o.moid },
        kind: 'traditional' as const,
        label: `${o.name} · ${MEDIA_MEDIUM_LABEL[o.medium as TraditionalMedium] ?? o.medium}`,
        platform: undefined as MediaSocialPlatform | undefined,
        medium: o.medium as TraditionalMedium,
      }));
    return [...social, ...traditional];
  }, [destinations.data]);

  const optionFor = (ref: SourceRef | null) =>
    ref ? destinationOptions.find((d) => sameSource(d.ref, ref)) : undefined;

  const payload = () => ({
    title: title.trim(),
    content_type: contentType,
    play_id: playId ? Number(playId) : null,
    series_id: seriesId ? Number(seriesId) : null,
    excerpt: excerpt.trim() || null,
    body,
    tags,
    scheduled_at: fromDatetimeLocal(scheduledAt),
  });

  /** Save, returning the id so callers can chain (queue, submit, navigate). */
  const persist = async (): Promise<number | null> => {
    const errors: ContentFormErrors = {};
    if (!title.trim()) {
      errors.title = 'Title is required';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return null;
    }
    setFormErrors({});
    try {
      if (isNew) {
        const created = await createContent.mutateAsync(payload());
        return created?.data?.mciid ?? null;
      }
      await updateContent.mutateAsync({ mciid, ...payload() });
      return mciid;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the content'));
      return null;
    }
  };

  const save = async () => {
    const savedId = await persist();
    if (savedId == null) return;
    toast.success(isNew ? 'Content created' : 'Content saved');
    if (isNew) navigate(`/admin/marketing/studio/content/${savedId}`);
  };

  const addVariant = async () => {
    if (isNew) {
      toast.error('Save the content first — a variant has to belong to something');
      return;
    }
    const option = destinationOptions.find((d) => d.key === addSource);
    if (!option) {
      toast.error('Pick a destination first');
      return;
    }
    const format = option.platform
      ? defaultFormatForPlatform(option.platform)
      : defaultFormatForPlatform(undefined, option.medium);

    setSavingVariant(true);
    try {
      await createVariant.mutateAsync({
        mciid,
        source_kind: option.ref.sourceKind,
        said: option.ref.sourceKind === 'account' ? option.ref.sourceId : null,
        moid: option.ref.sourceKind === 'outlet' ? option.ref.sourceId : null,
        variant_format: format,
        // Seed from the excerpt so a new variant starts from the master copy
        // rather than an empty box.
        body: excerpt.trim() || title.trim() || '',
      });
      setAddSource('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not add the variant'));
    } finally {
      setSavingVariant(false);
    }
  };

  const patchVariant = (variant: ApiContentVariant, changes: Record<string, unknown>) =>
    updateVariant.mutate(
      { mcvid: variant.mcvid, ...changes },
      { onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the variant')) },
    );

  const doQueue = async () => {
    const savedId = await persist();
    if (savedId == null) return;
    if (variants.length === 0) {
      toast.error('Add at least one destination variant to queue');
      return;
    }
    queueVariants.mutate(
      { mciid: savedId },
      {
        onSuccess: (res) => {
          const social = res?.data?.social_created ?? 0;
          const traditional = res?.data?.traditional_created ?? 0;
          const skipped = res?.data?.skipped ?? [];
          if (social + traditional === 0) {
            // Queueing is idempotent — everything already materialised. That is
            // a no-op, not a failure, and must not read as one.
            toast.message('Nothing new to queue', {
              description: 'Every variant is already in a distribution queue.',
            });
            return;
          }
          toast.success(
            `Queued ${social + traditional} — ${social} social, ${traditional} traditional` +
              (skipped.length ? `, ${skipped.length} already queued` : ''),
          );
          if (social > 0) navigate('/admin/marketing/studio/social');
          else navigate('/admin/marketing/studio/traditional');
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not queue the variants')),
      },
    );
  };

  const doSubmit = async () => {
    const savedId = await persist();
    if (savedId == null) return;
    submitContent.mutate(
      { mciid: savedId },
      {
        onSuccess: () => toast.success('Submitted for review'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not submit')),
      },
    );
  };

  const remove = () => {
    if (isNew) return;
    deleteContent.mutate(
      { mciid },
      {
        onSuccess: () => {
          toast.success('Content removed');
          navigate('/admin/marketing/studio/content');
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the content')),
      },
    );
  };

  const saving = createContent.isPending || updateContent.isPending;
  const status = (existing?.status ?? 'idea') as ContentStatus;
  /** Past the gate: moved by submit / approve / the worker, never by picking. */
  const gated = !(OPEN_STATUSES as string[]).includes(status);

  if (!isNew && contentQ.isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading content…
      </div>
    );
  }

  if (!isNew && contentQ.isError) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-12 text-center text-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load this content</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(contentQ.error, 'The marketing API did not respond')}
          </div>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/admin/marketing/studio/content">Back to Content</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="gap-1.5">
            <Link to="/admin/marketing/studio/content">
              <ArrowLeft className="h-4 w-4" /> Content
            </Link>
          </Button>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FileText className="h-5 w-5 text-gray-400" />
            {isNew ? 'New content' : (existing?.title ?? 'Edit content')}
          </h1>
          {!isNew && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium capitalize text-gray-700">
              {CONTENT_STATUS_LABEL[status] ?? status}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && (
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={doQueue} disabled={saving || isNew}>
            <Plus className="mr-1.5 h-4 w-4" /> Queue variants
          </Button>
          <Button type="button" variant="outline" onClick={doSubmit} disabled={saving || isNew}>
            <Send className="mr-1.5 h-4 w-4" /> Submit for review
          </Button>
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {status === 'in_review' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">This is in review.</p>
            <p className="text-xs">
              Clearing it needs the approve permission and stamps whoever does it —{' '}
              <Link
                to="/admin/marketing/crew/approvals"
                className="underline underline-offset-2"
              >
                Approvals
              </Link>
              . Editing is still allowed; the reviewer sees the current copy.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Field label={<><span>Title</span> <RequiredMark /></>}>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setFormErrors((errs) => clearFieldError(errs, 'title'));
              }}
              className={fieldClass(inputClass, formErrors.title)}
              aria-invalid={!!formErrors.title}
              placeholder="What is this piece called?"
            />
            <FieldHint error={formErrors.title} />
          </Field>

          <Field label="Excerpt">
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="min-h-[64px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
              placeholder="The one-line version. New variants start from this."
            />
          </Field>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Body</span>
            <RichEditor value={body} onChange={setBody} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Variants</h2>
              <span className="text-[11px] text-gray-400">
                One per destination. Queueing materialises the un-queued ones.
              </span>
            </div>

            {isNew ? (
              <p className="text-xs text-gray-400">
                Save this piece first — a variant has to belong to something.
              </p>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap gap-2">
                  <select
                    value={addSource}
                    onChange={(e) => setAddSource(e.target.value)}
                    className={`${inputClass} max-w-sm`}
                  >
                    <option value="">Add a destination…</option>
                    {destinationOptions
                      .filter((d) => !variants.some((v) => sameSource(refFromApi(v) ?? undefined, d.ref)))
                      .map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addVariant}
                    disabled={savingVariant || !addSource}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add
                  </Button>
                </div>

                {variantsQ.isLoading && (
                  <p className="text-xs text-gray-400">
                    <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading variants…
                  </p>
                )}

                {!variantsQ.isLoading && variants.length === 0 && (
                  <p className="text-xs text-gray-400">
                    No variants yet. Add one per account or outlet you want this to go to.
                  </p>
                )}

                <div className="space-y-3">
                  <TooltipProvider delayDuration={200}>
                  {variants.map((variant) => {
                    const ref = refFromApi(variant);
                    const option = optionFor(ref);
                    const formats =
                      variant.source_kind === 'account' ? SOCIAL_FORMATS : TRADITIONAL_FORMATS;
                    return (
                      <div
                        key={variant.mcvid}
                        className={cn(
                          'rounded-md border p-3',
                          variant.queued_at ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200',
                        )}
                      >
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          {option?.platform && (
                            <PlatformLogoBadge platform={option.platform} size="sm" />
                          )}
                          <span className="text-sm font-medium text-gray-800">
                            {option?.label ??
                              (ref
                                ? `${ref.sourceKind} ${ref.sourceId} (removed)`
                                : 'Unknown destination')}
                          </span>
                          {variant.queued_at && (
                            <span
                              className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                              title={`Queued ${new Date(variant.queued_at).toLocaleString()}`}
                            >
                              queued
                            </span>
                          )}
                          <select
                            value={variant.variant_format}
                            onChange={(e) =>
                              patchVariant(variant, { variant_format: e.target.value })
                            }
                            className="ml-auto h-8 rounded-md border border-gray-200 bg-white px-2 text-xs"
                          >
                            {formats.map((f: ContentFormat) => (
                              <option key={f} value={f}>
                                {CONTENT_FORMAT_LABEL[f]}
                              </option>
                            ))}
                          </select>
                          <ActionIconButton
                            label="Remove variant"
                            size="sm"
                            onClick={() =>
                              deleteVariant.mutate(
                                { mcvid: variant.mcvid },
                                {
                                  onError: (err) =>
                                    toast.error(
                                      getApiErrorMessage(err, 'Could not remove the variant'),
                                    ),
                                },
                              )
                            }
                            className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </ActionIconButton>
                        </div>
                        <textarea
                          defaultValue={variant.body ?? ''}
                          onBlur={(e) => {
                            if (e.target.value !== (variant.body ?? '')) {
                              patchVariant(variant, { body: e.target.value });
                            }
                          }}
                          className="min-h-[72px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                          placeholder="What goes out on this destination."
                        />
                        {variant.queued_at && (
                          <p className="mt-1 text-[11px] text-emerald-800">
                            Already queued — edits here do not change the copy already in the
                            distribution queue.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
            <Field label="Type">
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value as ContentType)}
                className={inputClass}
              >
                {(Object.keys(CONTENT_TYPE_LABEL) as ContentType[]).map((t) => (
                  <option key={t} value={t}>
                    {CONTENT_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </Field>

            {!isNew && (
              <Field label="Status">
                <select
                  value={gated ? '' : status}
                  onChange={(e) => {
                    // The mutation's own type only admits the ungated three, so
                    // the placeholder option (which carries the gated status)
                    // cannot be sent by accident.
                    const next = e.target.value as OpenStatus;
                    if (!OPEN_STATUSES.includes(next)) return;
                    setStatusMutation.mutate(
                      { mciid, status: next },
                      {
                        onSuccess: () => toast.success('Status updated'),
                        onError: (err) =>
                          toast.error(getApiErrorMessage(err, 'Could not change the status')),
                      },
                    );
                  }}
                  className={inputClass}
                  disabled={gated}
                >
                  {gated && (
                    <option value="">{CONTENT_STATUS_LABEL[status] ?? status}</option>
                  )}
                  {OPEN_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {CONTENT_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] text-gray-400">
                  {gated
                    ? 'Past the gate — moved by submitting, approving, or the publish worker.'
                    : 'Review and approval are reached by submitting, not by picking them here.'}
                </span>
              </Field>
            )}

            <Field label="Play">
              <select
                value={playId}
                onChange={(e) => setPlayId(e.target.value)}
                className={inputClass}
              >
                <option value="">No play linked</option>
                {playOptions.map((p) => (
                  <option key={p.play_id} value={String(p.play_id)}>
                    {p.name} · {PLAY_STATUS_LABEL[p.status as keyof typeof PLAY_STATUS_LABEL] ?? p.status}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Series">
              <select
                value={seriesId}
                onChange={(e) => setSeriesId(e.target.value)}
                className={inputClass}
              >
                <option value="">Not part of a series</option>
                {(seriesQ.data?.items ?? []).map((s) => (
                  <option key={s.series_id} value={String(s.series_id)}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Scheduled for">
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className={inputClass}
              />
            </Field>

            <div className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Tags</span>
              <ChipMultiSelect
                options={TAG_CHIP_OPTIONS}
                value={tags}
                onChange={(next) => {
                  setTags(next);
                  setFormErrors((errs) => clearFieldError(errs, 'tags'));
                }}
                aria-label="Content tags"
              />
              <FieldHint error={formErrors.tags} />
            </div>
          </div>

          {!isNew && existing?.approved_at && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
              Approved {new Date(existing.approved_at).toLocaleString()}
              {existing.approved_by ? ` by #${existing.approved_by}` : ''}.
            </div>
          )}
        </aside>
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete content?"
        description={`"${existing?.title ?? 'This piece'}" will be removed. Distribution rows already queued from it keep their copy but lose the link.`}
        onConfirm={remove}
      />
    </div>
  );
};

export default ContentEditorPage;
